'use client';

import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useSession } from 'next-auth/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authKeys } from '../queryKeys';
import { getCurrentUser, type CurrentUserData } from '../actions/getCurrentUser';

type CurrentUser = CurrentUserData;

interface CurrentUserContextValue {
  user: CurrentUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const CurrentUserContext = createContext<CurrentUserContextValue | null>(null);

/**
 * Maps NextAuth session to CurrentUser format.
 * Avoids a separate server action call - session is passed from server via SessionProvider.
 */
function sessionToUser(sessionUser: any): CurrentUser | null {
  if (!sessionUser?.id) return null;
  return {
    id: sessionUser.id,
    email: sessionUser.email || null,
    name: sessionUser.name || null,
    image: sessionUser.image || null,
    role: sessionUser.role || 'user',
    familyId: sessionUser.familyId || null,
    familyName: sessionUser.familyName || null,
    memberId: sessionUser.id, // memberId = userId in JWT
    provider: sessionUser.provider || null,
  };
}

export function CurrentUserProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  const queryClient = useQueryClient();

  // Derive user from NextAuth session (instant if session was passed server-side)
  const sessionUser = useMemo(
    () => sessionToUser(session?.user),
    [session?.user],
  );

  // Use React Query for the server action call (refetch case)
  const { data: queryUser, isLoading: queryLoading, error: queryError, refetch } = useQuery<CurrentUser | null>({
    queryKey: authKeys.currentUser,
    queryFn: () => getCurrentUser(),
    // Only enable when session is authenticated but sessionUser is missing
    // (edge case: session exists but we need full data refresh)
    enabled: status === 'authenticated' && !sessionUser,
  });

  const user = sessionUser ?? queryUser ?? null;
  const loading = status === 'loading' || (status === 'authenticated' && !sessionUser && queryLoading);
  const error = queryError instanceof Error ? queryError.message : null;

  const handleRefetch = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Memoize the context value: an inline object is a NEW reference every render,
  // so any re-render of this high provider re-rendered EVERY currentUser consumer
  // (the whole sidebar chrome) even when the user data was unchanged. Stable
  // reference → consumers only re-render when user/loading/error actually change.
  const value = useMemo(
    () => ({ user, isAuthenticated: !!user, loading, error, refetch: handleRefetch }),
    [user, loading, error, handleRefetch],
  );

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
}

export function useCurrentUserContext(): CurrentUserContextValue | null {
  return useContext(CurrentUserContext);
}
